import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { Download } from 'lucide-react';
import React, { useCallback, useMemo } from 'react';
import { type ColumnDefinition, ReusableTable } from '~/components/tables/ReusableTable';
import { CopyToClipboardButton } from '~/components/ui';
import type { TaxYearBasedProjectPlanRecord } from '~/types';
import { TaxYearPlanHelper } from './TaxYearPlanHelper';

interface TaxYearPlanTableProps {
  taxYearPlan: TaxYearBasedProjectPlanRecord[];
  title?: string;
  showSummary?: boolean;
  /** Bond series names for per-bond payment columns */
  bondNames?: string[];
  /** Project name for export filename */
  projectName?: string;
  /** Configuration name for export filename */
  configurationName?: string;
  /** Millage shape name for export filename */
  millageShapeName?: string;
}

// Convert tax year plan to array format for table display
const convertToTableData = (
  taxYearPlan: TaxYearBasedProjectPlanRecord[]
): (TaxYearBasedProjectPlanRecord & { [key: string]: unknown })[] => {
  return TaxYearPlanHelper.getSortedRecords(taxYearPlan).map((record) => ({
    ...record,
    // Add index signature to satisfy type constraint
    [Symbol.toStringTag]: 'TaxYearBasedProjectPlanRecord',
  }));
};

/**
 * Convert a string to a URL-friendly slug
 * - Converts to lowercase
 * - Replaces spaces with hyphens
 * - Removes non-alphanumeric characters (except hyphens)
 * - Collapses multiple hyphens into one
 */
const toSlug = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, '') // Remove non-alphanumeric except hyphens
    .replace(/-+/g, '-') // Collapse multiple hyphens
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Generate export filename with project-config-shape-tax-year-plan-datetime format
 */
const generateExportFilename = (
  projectName?: string,
  configurationName?: string,
  millageShapeName?: string
): string => {
  const parts: string[] = [];

  if (projectName) parts.push(toSlug(projectName));
  if (configurationName) parts.push(toSlug(configurationName));
  if (millageShapeName) parts.push(toSlug(millageShapeName));
  parts.push('tax-year-plan');

  // Add datetime to the minute (YYYY-MM-DD-HHmm)
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10); // YYYY-MM-DD
  const timeStr = now.toTimeString().slice(0, 5).replace(':', ''); // HHmm
  parts.push(`${dateStr}-${timeStr}`);

  return `${parts.join('-')}.csv`;
};

export const TaxYearPlanTable: React.FC<TaxYearPlanTableProps> = ({
  taxYearPlan,
  showSummary = true,
  bondNames = [],
  projectName,
  configurationName,
  millageShapeName,
}) => {
  // Convert to table data
  const tableData = convertToTableData(taxYearPlan);

  // Define column structure - using useMemo since bondNames affects columns
  const columns: ColumnDefinition<TaxYearBasedProjectPlanRecord & { [key: string]: unknown }>[] =
    useMemo(() => {
      const baseColumns: ColumnDefinition<
        TaxYearBasedProjectPlanRecord & { [key: string]: unknown }
      >[] = [
        {
          key: 'taxYear',
          header: 'Tax Year',
          accessor: 'taxYear',
          headerTextAlign: 'center',
          cellTextAlign: 'center',
        },
        {
          key: 'taxableValue',
          header: 'Taxable Value',
          accessor: (item) => TaxYearPlanHelper.formatCurrency(item.taxableValue),
          headerTextAlign: 'right',
          cellTextAlign: 'right',
        },
        {
          key: 'millage',
          header: 'Millage',
          accessor: (item) => TaxYearPlanHelper.formatMillage(item.millage),
          headerTextAlign: 'right',
          cellTextAlign: 'right',
        },
        {
          key: 'totalRevenue',
          header: 'Total Revenue',
          accessor: (item) => TaxYearPlanHelper.formatCurrency(item.totalRevenue),
          headerTextAlign: 'right',
          cellTextAlign: 'right',
        },
        {
          key: 'existingDebt',
          header: 'Existing Debt',
          accessor: (item) => TaxYearPlanHelper.formatCurrency(item.existingDebt),
          headerTextAlign: 'right',
          cellTextAlign: 'right',
        },
        {
          key: 'netRevenue',
          header: 'Net Revenue',
          accessor: (item) => TaxYearPlanHelper.formatCurrency(item.netRevenue),
          headerTextAlign: 'right',
          cellTextAlign: 'right',
        },
      ];

      // Add per-bond principal columns if bond names are provided
      const bondColumns: ColumnDefinition<
        TaxYearBasedProjectPlanRecord & { [key: string]: unknown }
      >[] = bondNames.map((name, index) => ({
        key: `bond_${index}`,
        header: `${name} Principal`,
        accessor: (item) => {
          const principal = item.bondPayments?.[index] ?? 0;
          return TaxYearPlanHelper.formatCurrency(principal);
        },
        headerTextAlign: 'right' as const,
        cellTextAlign: 'right' as const,
      }));

      const remainingColumns: ColumnDefinition<
        TaxYearBasedProjectPlanRecord & { [key: string]: unknown }
      >[] = [
        {
          key: 'totalBondPayments',
          header: 'Total Bond Payments',
          accessor: (item) => TaxYearPlanHelper.formatCurrency(item.totalBondPayments),
          headerTextAlign: 'right',
          cellTextAlign: 'right',
        },
        {
          key: 'totalPayment',
          header: 'Total Payments',
          accessor: (item) => TaxYearPlanHelper.formatCurrency(item.totalPayment),
          headerTextAlign: 'right',
          cellTextAlign: 'right',
        },
        {
          key: 'drfAdjustment',
          header: 'DRF Adjustment',
          accessor: (item) => TaxYearPlanHelper.formatCurrency(item.drfAdjustment),
          headerTextAlign: 'right',
          cellTextAlign: 'right',
        },
        {
          key: 'drfEndingBalance',
          header: 'DRF Ending Balance',
          accessor: (item) => TaxYearPlanHelper.formatCurrency(item.drfEndingBalance),
          headerTextAlign: 'right',
          cellTextAlign: 'right',
        },
      ];

      return [...baseColumns, ...bondColumns, ...remainingColumns];
    }, [bondNames]);

  // Calculate summary if requested - must be before early returns
  const summary = showSummary ? TaxYearPlanHelper.calculateFinancialSummary(taxYearPlan) : null;

  // Calculate per-bond principal totals for display in summary
  const bondPrincipalTotals = useMemo(() => {
    if (!taxYearPlan || taxYearPlan.length === 0 || bondNames.length === 0) return [];
    const sortedData = TaxYearPlanHelper.getSortedRecords(taxYearPlan);
    return bondNames.map((_, index) =>
      sortedData.reduce((sum, r) => sum + (r.bondPayments[index] ?? 0), 0)
    );
  }, [taxYearPlan, bondNames]);

  // Create footer row data for totals - must be before early returns
  const footerRowData = React.useMemo(() => {
    if (!taxYearPlan || taxYearPlan.length === 0) return null;
    if (!summary) return null;

    const footerData: Partial<TaxYearBasedProjectPlanRecord & { [key: string]: unknown }> = {
      taxYear: -1, // Sentinel value for totals row
      taxableValue: 0, // No sum for taxable value
      millage: 0, // No sum for millage
      totalRevenue: summary.totalRevenue,
      existingDebt: summary.totalExistingDebt,
      netRevenue: summary.totalNetRevenue,
      totalBondPayments: summary.totalBondPayments,
      totalPayment: summary.totalPayments,
      drfAdjustment: summary.totalDrfAdjustment,
      drfEndingBalance: summary.finalDrfBalance,
    };

    return footerData;
  }, [summary, taxYearPlan]);

  // Export to CSV function
  const handleExportCSV = useCallback(() => {
    if (!taxYearPlan || taxYearPlan.length === 0) return;

    const sortedData = TaxYearPlanHelper.getSortedRecords(taxYearPlan);

    // Helper to format principal with both full and thousands versions
    const formatPrincipalPair = (value: number): string[] => [
      value.toFixed(2), // Full number (e.g., 370000.00)
      Math.round(value / 1000).toString(), // Rounded to thousands (e.g., 370)
    ];

    // Build header row - includes both full and (K) columns for each bond principal
    const bondHeaders = bondNames.flatMap((name) => [`${name} Principal`, `${name} Principal (K)`]);

    const headers = [
      'Tax Year',
      'Taxable Value',
      'Millage',
      'Total Revenue',
      'Existing Debt',
      'Net Revenue',
      ...bondHeaders,
      'Total Bond Payments',
      'Total Bond Payments (K)',
      'Total Payments',
      'DRF Adjustment',
      'DRF Ending Balance',
    ];

    // Build data rows
    const rows = sortedData.map((record) => {
      const bondPrincipalPairs = bondNames.flatMap((_, index) =>
        formatPrincipalPair(record.bondPayments[index] ?? 0)
      );

      return [
        record.taxYear.toString(),
        record.taxableValue.toFixed(2),
        record.millage.toFixed(2), // 2 decimal places for millage
        record.totalRevenue.toFixed(2),
        record.existingDebt.toFixed(2),
        record.netRevenue.toFixed(2),
        ...bondPrincipalPairs,
        record.totalBondPayments.toFixed(2),
        Math.round(record.totalBondPayments / 1000).toString(),
        record.totalPayment.toFixed(2),
        record.drfAdjustment.toFixed(2),
        record.drfEndingBalance.toFixed(2),
      ];
    });

    // Add totals row if we have summary
    if (summary) {
      const bondTotalPairs = bondNames.flatMap((_, index) => {
        const total = sortedData.reduce((sum, r) => sum + (r.bondPayments[index] ?? 0), 0);
        return formatPrincipalPair(total);
      });

      rows.push([
        'TOTAL',
        '', // No total for taxable value
        '', // No total for millage
        summary.totalRevenue.toFixed(2),
        summary.totalExistingDebt.toFixed(2),
        summary.totalNetRevenue.toFixed(2),
        ...bondTotalPairs,
        summary.totalBondPayments.toFixed(2),
        Math.round(summary.totalBondPayments / 1000).toString(),
        summary.totalPayments.toFixed(2),
        summary.totalDrfAdjustment.toFixed(2),
        summary.finalDrfBalance.toFixed(2),
      ]);
    }

    // Convert to CSV string
    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      generateExportFilename(projectName, configurationName, millageShapeName)
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [taxYearPlan, bondNames, projectName, configurationName, millageShapeName, summary]);

  // Generate text to copy for millage rates
  const getMillageText = useCallback(() => {
    if (!taxYearPlan || taxYearPlan.length === 0) return '';
    const sortedData = TaxYearPlanHelper.getSortedRecords(taxYearPlan);
    // Extract millage values and format as newline-separated text
    // Excel will paste these into separate rows when pasted into a column
    const millageValues = sortedData.map((record) => record.millage.toFixed(2));
    return millageValues.join('\n');
  }, [taxYearPlan]);

  // Generate text to copy for bond principal payments (in thousands)
  const getBondText = useCallback(
    (bondIndex: number) => {
      if (!taxYearPlan || taxYearPlan.length === 0) return '';
      const sortedData = TaxYearPlanHelper.getSortedRecords(taxYearPlan);
      // Extract principal payments for this bond, convert to thousands, and format as newline-separated text
      const principalValues = sortedData.map((record) => {
        const principal = record.bondPayments?.[bondIndex] ?? 0;
        return Math.round(principal / 1000).toString();
      });
      return principalValues.join('\n');
    },
    [taxYearPlan]
  );

  // Handle empty data - must be after hooks
  if (!taxYearPlan || taxYearPlan.length === 0) {
    return (
      <Box>
        <Text color="gray.500">No tax year plan data available</Text>
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary section - horizontal layout with export button */}
      {summary && (
        <Box
          mb={4}
          p={4}
          bg="gray.50"
          borderRadius="md"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <HStack gap={6} wrap="wrap" align="flex-start">
            <VStack align="flex-start" gap={0}>
              <Text fontSize="xs" color="gray.600">
                Total Revenue
              </Text>
              <Text fontSize="sm" fontWeight="semibold">
                {TaxYearPlanHelper.formatCurrency(summary.totalRevenue)}
              </Text>
            </VStack>
            <VStack align="flex-start" gap={0}>
              <Text fontSize="xs" color="gray.600">
                Total Existing Debt
              </Text>
              <Text fontSize="sm" fontWeight="semibold">
                {TaxYearPlanHelper.formatCurrency(summary.totalExistingDebt)}
              </Text>
            </VStack>
            {bondNames.map((bondName, index) => (
              <VStack key={bondName} align="flex-start" gap={0}>
                <Text fontSize="xs" color="gray.600">
                  {bondName} Principal
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {TaxYearPlanHelper.formatCurrency(bondPrincipalTotals[index] ?? 0)}
                </Text>
              </VStack>
            ))}
            <VStack align="flex-start" gap={0}>
              <Text fontSize="xs" color="gray.600">
                Total DRF Adjustment
              </Text>
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={summary.totalDrfAdjustment >= 0 ? 'green.600' : 'red.600'}
              >
                {TaxYearPlanHelper.formatCurrency(summary.totalDrfAdjustment)}
              </Text>
            </VStack>
            <VStack align="flex-start" gap={0}>
              <Text fontSize="xs" color="gray.600">
                Final DRF Balance
              </Text>
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={summary.finalDrfBalance >= 0 ? 'green.600' : 'red.600'}
              >
                {TaxYearPlanHelper.formatCurrency(summary.finalDrfBalance)}
              </Text>
            </VStack>
          </HStack>
          <HStack gap={2} wrap="wrap">
            <CopyToClipboardButton
              size="sm"
              variant="outline"
              colorPalette="gray"
              textToCopy={getMillageText}
            >
              Copy Millage
            </CopyToClipboardButton>
            {bondNames.map((bondName, index) => (
              <CopyToClipboardButton
                key={bondName}
                size="sm"
                variant="outline"
                colorPalette="gray"
                textToCopy={() => getBondText(index)}
              >
                Copy {bondName}
              </CopyToClipboardButton>
            ))}
            <Button size="sm" variant="outline" onClick={handleExportCSV} colorPalette="blue">
              <Download size={14} />
              <Text ml={1}>Export CSV</Text>
            </Button>
          </HStack>
        </Box>
      )}

      {/* Data table */}
      <ReusableTable columns={columns} data={tableData} footerRow={footerRowData} />

      {/* Footer information */}
      <Text mt={2} fontSize="sm" color="gray.600">
        {tableData.length} year{tableData.length !== 1 ? 's' : ''} in tax year plan
      </Text>
    </Box>
  );
};
